import {
  Component,
  OnInit,
  AfterViewInit,
  ViewChild,
  ElementRef,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { finalize } from 'rxjs';
import { AuthService } from '@auth/services/auth.service';
import { FeedbackService } from '@core/services/feedback.service';

function passwordsMatchValidator(group: AbstractControl): ValidationErrors | null {
  const novaPassword = group.get('novaPassword');
  const confirmar = group.get('confirmarNovaPassword');
  if (novaPassword && confirmar && novaPassword.value !== confirmar.value) {
    confirmar.setErrors({ ...confirmar.errors, passwordMismatch: true });
  } else if (confirmar?.errors?.['passwordMismatch']) {
    const errors = { ...confirmar.errors };
    delete errors['passwordMismatch'];
    confirmar.setErrors(Object.keys(errors).length ? errors : null);
  }
  return null;
}

@Component({
  selector: 'app-primeiro-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InputTextModule, RouterLink],
  providers: [],
  templateUrl: './primeiro-login.component.html',
  styleUrl: './primeiro-login.component.scss',
})
export class PrimeiroLoginComponent implements OnInit, AfterViewInit {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly feedback = inject(FeedbackService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);

  @ViewChild('novaPasswordInput') novaPasswordInput?: ElementRef<HTMLInputElement>;

  form!: FormGroup;
  loading = false;
  /** Indica se username e/ou passwordTemporaria vieram pré-preenchidos pelo link recebido. */
  preenchidoPorLink = false;

  ngOnInit(): void {
    const qp = this.activatedRoute.snapshot.queryParamMap;
    const usernameFromQuery = (qp.get('username') ?? '').trim();
    const passwordTemporariaFromQuery = (qp.get('passwordTemporaria') ?? '').trim();

    this.preenchidoPorLink = !!usernameFromQuery || !!passwordTemporariaFromQuery;

    this.form = this.fb.group(
      {
        username: [usernameFromQuery, [Validators.required]],
        passwordTemporaria: [passwordTemporariaFromQuery, [Validators.required]],
        novaPassword: [null, [Validators.required, Validators.minLength(8)]],
        confirmarNovaPassword: [null, [Validators.required]],
      },
      { validators: passwordsMatchValidator },
    );

    // Não deixar a password temporária visível na barra de endereço/histórico.
    if (qp.get('username') || qp.get('passwordTemporaria')) {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: {},
        replaceUrl: true,
      });
    }
  }

  ngAfterViewInit(): void {
    if (this.preenchidoPorLink) {
      this.novaPasswordInput?.nativeElement.focus();
    }
  }

  get f() {
    return this.form.controls;
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.feedback.showWarning('Preencha os campos obrigatórios e garanta que as palavras-passe coincidem.', {
        summary: 'Formulário Inválido',
        life: 4000,
      });
      return;
    }

    this.loading = true;
    const { username, passwordTemporaria, novaPassword } = this.form.value;

    this.authService
      .loginFirst(username, passwordTemporaria, novaPassword)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => {
          if (
            this.authService.isLoggedIn() &&
            this.authService.isAdmin()
          ) {
            this.feedback.showSuccess('Palavra-passe definida. Sessão iniciada.', {
              summary: 'Sucesso',
              life: 3000,
            });
            this.router.navigate(['/admin']);
          } else if (this.authService.isLoggedIn()) {
            this.authService.logout();
            this.feedback.showWarning(
              'O utilizador não possui acesso ao Backoffice.',
              {
                summary: 'Acesso Não Autorizado',
                life: 5500,
              },
            );
          } else {
            this.feedback.showSuccess('Palavra-passe definida com sucesso. Faça login com a nova palavra-passe.', {
              summary: 'Sucesso',
              life: 5000,
            });
            this.router.navigate(['/login']);
          }
        },
        error: (err) => {
          const detail = this.feedback.extractErrorMessage(
            err,
            'Utilizador ou palavra-passe temporária inválidos.',
          );

          this.feedback.showError(err, {
            summary: 'Falha na Validação',
            detail,
            life: 5500,
          });
        },
      });
  }
}
